import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Gift } from "lucide-react";
import { toast } from "sonner";

export default function AdminPromotions() {
  const [promos, setPromos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [clientTags, setClientTags] = useState([]);

  const load = async () => {
    const [promosResp, tagsResp] = await Promise.all([
      api.get("/admin/promotions"),
      api.get("/admin/client-tags"),
    ]);
    setPromos(promosResp.data || []);
    setClientTags(tagsResp.data?.tags || []);
  };

  useEffect(() => { load(); }, []);

  const updatePromo = (code, patch) => {
    setPromos((prev) => prev.map((p) => p.code === code ? { ...p, ...patch } : p));
  };

  const createPromo = async () => {
    setSaving(true);
    try {
      await api.post("/admin/promotions", {
        title: "Nueva promoción",
        description: "Describe aquí la promoción.",
        gift: "Indica aquí qué debe aplicar KAPEI",
        active: false,
        audience: "all",
        single_use: true,
        stackable: false,
        max_uses_per_user: 1,
        max_total_uses: 0,
      });
      toast.success("Promoción creada");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const notifyPromo = async (promo) => {
    if (!window.confirm(`¿Enviar notificación de esta promoción a los clientes afectados?\n\n${promo.title || promo.code}`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/promotions/${promo.code}/notify`);
      toast.success(`Notificados: ${data.notified}. Push OK: ${data.push_ok}.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const savePromo = async (promo) => {
    setSaving(true);
    try {
      await api.put(`/admin/promotions/${promo.code}`, promo);
      toast.success("Promoción guardada");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl">
      <div className="mb-6 flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#4d7c0f] text-white">
          <Gift className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-3xl font-black text-slate-900">Promociones</h1>
          <p className="text-sm text-slate-600">Activa, desactiva y modifica ofertas de la App KAPEI.</p>
        </div>
        <Button onClick={createPromo} disabled={saving} className="bg-[#4d7c0f] hover:bg-[#3f6212]">
          Nueva promoción
        </Button>
      </div>

      <div className="flex flex-col gap-5">
        {promos.map((promo) => (
          <section key={promo.code} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-xs font-black uppercase tracking-widest text-slate-500">{promo.code}</div>
                <h2 className="text-xl font-black text-slate-900">{promo.title || "Promoción sin título"}</h2>
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
                <Label className="font-bold">Activa</Label>
                <Switch
                  checked={!!promo.active}
                  onCheckedChange={(v) => updatePromo(promo.code, { active: v })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Nombre interno</Label>
                <Input value={promo.name || ""} onChange={(e) => updatePromo(promo.code, { name: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Título visible</Label>
                <Input value={promo.title || ""} onChange={(e) => updatePromo(promo.code, { title: e.target.value })} className="mt-1" />
              </div>
              <div className="md:col-span-2">
                <Label>Descripción visible</Label>
                <Textarea value={promo.description || ""} onChange={(e) => updatePromo(promo.code, { description: e.target.value })} className="mt-1 min-h-[90px]" />
              </div>
              <div className="md:col-span-2">
                <Label>Qué debe aplicar KAPEI</Label>
                <Input value={promo.gift || ""} onChange={(e) => updatePromo(promo.code, { gift: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Destinatario</Label>
                <Select
                  value={promo.audience || "all"}
                  onValueChange={(v) => updatePromo(promo.code, { audience: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Destinatario" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los clientes</SelectItem>
                    <SelectItem value="first_order">Primer pedido</SelectItem>
                    <SelectItem value="new_clients">Nuevos clientes</SelectItem>
                    <SelectItem value="inactive_30">Sin pedidos en 30 días</SelectItem>
                    <SelectItem value="inactive_60">Sin pedidos en 60 días</SelectItem>
                    <SelectItem value="student">Tipo: Estudiante</SelectItem>
                    <SelectItem value="business">Tipo: Empresa</SelectItem>
                    <SelectItem value="vip">Tipo: VIP</SelectItem>
                    <SelectItem value="preferred">Tipo: Preferente</SelectItem>
                    <SelectItem value="tag">Etiqueta concreta</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {promo.audience === "tag" && (
                <div>
                  <Label>Etiqueta requerida</Label>
                  <Select
                    value={promo.required_tag || ""}
                    onValueChange={(v) => updatePromo(promo.code, { required_tag: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Elige etiqueta" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientTags.map((tag) => (
                        <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Usos por cliente</Label>
                <Input
                  type="number"
                  min={1}
                  value={promo.max_uses_per_user || (promo.single_use === false ? 99 : 1)}
                  onChange={(e) => updatePromo(promo.code, { max_uses_per_user: parseInt(e.target.value || "1", 10) })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Usos totales de campaña</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder="0 = sin límite"
                  value={promo.max_total_uses || 0}
                  onChange={(e) => updatePromo(promo.code, { max_total_uses: parseInt(e.target.value || "0", 10) })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Inicio</Label>
                <Input
                  type="date"
                  value={promo.starts_at || ""}
                  onChange={(e) => updatePromo(promo.code, { starts_at: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Fin</Label>
                <Input
                  type="date"
                  value={promo.ends_at || ""}
                  onChange={(e) => updatePromo(promo.code, { ends_at: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
                <Label className="font-bold">Un solo uso por cliente</Label>
                <Switch
                  checked={promo.single_use !== false}
                  onCheckedChange={(v) => updatePromo(promo.code, { single_use: v })}
                />
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
                <Label className="font-bold">Acumulable</Label>
                <Switch
                  checked={!!promo.stackable}
                  onCheckedChange={(v) => updatePromo(promo.code, { stackable: v })}
                />
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2 md:flex-row md:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => notifyPromo(promo)}
                disabled={saving || !promo.active}
              >
                Notificar clientes
              </Button>
              <Button onClick={() => savePromo(promo)} disabled={saving} className="bg-[#4d7c0f] hover:bg-[#3f6212]">
                {saving ? "Guardando…" : "Guardar promoción"}
              </Button>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
